import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Users, 
  Calendar, 
  Clock,
  UserPlus, 
  MapPin, 
  Info, 
  Dumbbell, 
  Loader2, 
  CheckCircle2 
} from 'lucide-react';
import { useGroupActivityStore } from '@/store/groupActivityStore';
import { GroupActivity } from '@/types/groupActivity';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { CitySearchCombobox } from '@/components/ui/combobox/CitySearchCombobox';
import UnifiedVenueSelector from '@/components/facilities/venues-api/components/UnifiedVenueSelector';
import UserSearchSelect, { SelectedUser } from './UserSearchSelect';

interface NewGroupActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewGroupActivityModal: React.FC<NewGroupActivityModalProps> = ({ 
  isOpen, 
  onClose 
}) => {
  const { isDarkMode } = useTheme();
  const { createGroupActivity, error, clearError } = useGroupActivityStore();
  
  const [step, setStep] = useState<'details' | 'venue' | 'success'>('details');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<Partial<GroupActivity>>({
    title: '',
    description: '',
    sport: '',
    groupDate: new Date().toISOString().split('T')[0],
    groupTime: '18:00',
    maxParticipants: 10,
    invitedMembers: []
  });
  const [venueData, setVenueData] = useState({
    venueName: '',
    venueAddress: '',
    venueCity: ''
  });
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);
  const [showInviteSection, setShowInviteSection] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('details');
      setFormData({
        title: '',
        description: '',
        sport: '',
        groupDate: new Date().toISOString().split('T')[0],
        groupTime: '18:00',
        maxParticipants: 10,
        invitedMembers: []
      });
      setVenueData({
        venueName: '',
        venueAddress: '',
        venueCity: ''
      });
      setSelectedUsers([]);
      setShowInviteSection(false);
      clearError();
    }
  }, [isOpen, clearError]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleVenueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setVenueData(prev => ({ ...prev, [name]: value }));
  };

  const handleCityChange = (city: string) => {
    setVenueData(prev => ({ ...prev, venueCity: city }));
  };

  const validateDetailsForm = () => {
    return (
      formData.title?.trim() !== '' &&
      formData.sport?.trim() !== '' &&
formData.groupDate &&
formData.groupTime
    );
  };

  const validateVenueForm = () => {
    return venueData.venueCity.trim() !== '';
  };

  const handleNextStep = () => {
    if (step === 'details' && validateDetailsForm()) {
      setStep('venue');
    }
  };

  const handlePrevStep = () => {
    if (step === 'venue') {
      setStep('details');
    }
  };

  const handleSubmit = async () => {
    if (!validateDetailsForm() || !validateVenueForm()) return;

    setIsSubmitting(true);
    try {
      // Combine form data and venue data
      const completeData = {
        ...formData,
        invitedMembers: selectedUsers.map(user => user.auth_id),
        venueName: venueData.venueName,
        venueAddress: venueData.venueAddress,
        venueCity: venueData.venueCity
      };

      await createGroupActivity(completeData);
      setStep('success');
    } catch (error) {
      console.error('Failed to create group activity:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  // Sports options
  const sportsOptions = [
  { value: '🎾 Padel', label: '🎾 Padel' },
  { value: '🎾 Tennis', label: '🎾 Tennis' },
  { value: '⚽ Football', label: '⚽ Football' },
  { value: '🏃 Running', label: '🏃 Running' },
  { value: '🚴 Cycling', label: '🚴 Cycling' },
  { value: '🧘 Yoga', label: '🧘 Yoga' },
  { value: '🤸 Pilates', label: '🤸 Pilates' },
  { value: '⛳ Golf', label: '⛳ Golf' },
  { value: '🏀 Basketball', label: '🏀 Basketball' },
  { value: '🏸 Badminton', label: '🏸 Badminton' },
  { value: '🏊 Swimming', label: '🏊 Swimming' },
  { value: '🏐 Volleyball', label: '🏐 Volleyball' },
  { value: '🏋️ Gym', label: '🏋️ Gym' },
  { value: '🥊 Boxing', label: '🥊 Boxing' },
  { value: '🏓 Table Tennis', label: '🏓 Table Tennis' },
  { value: '🏄 Surfing', label: '🏄 Surfing' },
  { value: '🤾 Handball', label: '🤾 Handball' },
  { value: '🏉 Rugby', label: '🏉 Rugby' },
  { value: '🏏 Cricket', label: '🏏 Cricket' },
  { value: '🕺 Zumba', label: '🕺 Zumba' },
  { value: '💃 Dance Fitness', label: '💃 Dance Fitness' },
  { value: '🧗 Climbing', label: '🧗 Climbing' },
  { value: '🥋 Martial Arts', label: '🥋 Martial Arts' }
];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className={cn(
        "sm:max-w-[550px]",
        isDarkMode ? "bg-dark-200 border-dark-300" : "bg-white border-gray-200"
      )}>
        {step === 'success' ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-6">
              <CheckCircle2 className="h-8 w-8 text-white" />
            </div>
           <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-2">
  Group Activity Created!
</h2>
<p className="text-center text-gray-700 dark:text-white/80 mb-6">
  Your group activity has been created successfully. Others can now join and participate.
</p>
            
            <Button 
              onClick={onClose}
              className="bg-gold-400 hover:bg-gold-500 text-dark-50 w-full"
            >
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
             <DialogTitle className={cn("flex items-center gap-2", isDarkMode ? "text-white" : "text-dark-800")}>
                <Users className="h-5 w-5 text-gold-400" />
                <span>Create Group Activity</span>
              </DialogTitle>
            </DialogHeader>

            {/* Step Indicator */}
            <div className="flex items-center justify-center mb-4">
              <div className={cn(
                "flex items-center justify-center w-8 h-8 rounded-full",
                step === 'details' 
                  ? "bg-gold-400 text-dark-50" 
                  : "bg-dark-300 text-dark-800"
              )}>
                1
              </div>
              <div className={cn(
                "w-8 h-1",
                step === 'details' 
                  ? "bg-dark-300" 
                  : "bg-gold-400"
              )} />
              <div className={cn(
                "flex items-center justify-center w-8 h-8 rounded-full",
                step === 'venue' 
                  ? "bg-gold-400 text-dark-50" 
                  : "bg-dark-300 text-dark-800"
              )}>
                2
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 text-red-500 p-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            {step === 'details' && (
              <div className="space-y-4">
                {/* Title */}
                <div className="space-y-2">
                 <Label className={cn("flex items-center", isDarkMode ? "text-white" : "text-dark-800")}>
  Activity Title

                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Input
                    name="title"
                    value={formData.title || ''}
                    onChange={handleInputChange}
                    placeholder="e.g., Sunday Tennis Group"
                    className={cn(
                      "bg-dark-300 border-dark-400 text-white",
                      "focus:border-gold-400 focus:ring-gold-400/20"
                    )}
                  />
                </div>

                {/* Sport */}
                <div className="space-y-2">
<Label className={cn("flex items-center", isDarkMode ? "text-white" : "text-dark-800")}>
  Sport

                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <div className="relative">
                  <div className="relative">
  <Dumbbell className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-dark-800 pointer-events-none" />
  <select
    name="sport"
    value={formData.sport || ''}
    onChange={handleInputChange}
    className={cn(
      "w-full pl-9 bg-dark-300 border-dark-400 text-white rounded-md h-10 appearance-none",
      "focus:border-gold-400 focus:ring-gold-400/20"
    )}
    required
  >
    <option value="" disabled>Select a sport</option>
    {sportsOptions.map(option => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
</div>

                  </div>
                </div>
                {/* Date and Time */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={cn("flex items-center", isDarkMode ? "text-white" : "text-dark-800")}>
  Date

                      <span className="text-red-500 ml-1">*</span>
                    </Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-dark-800" />
                      <Input
                        type="date"
                        name="groupDate"
                        value={formData.groupDate || ''}
                        onChange={handleInputChange}
                        min={new Date().toISOString().split('T')[0]}
                        className={cn(
                          "pl-10 bg-dark-300 border-dark-400 text-white",
                          "focus:border-gold-400 focus:ring-gold-400/20"
                        )}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                   <Label className={cn("flex items-center", isDarkMode ? "text-white" : "text-dark-800")}>
  Time

                      <span className="text-red-500 ml-1">*</span>
                    </Label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-dark-800" />
                      <Input
                        type="time"
                        name="groupTime"
                        value={formData.groupTime || ''}
                        onChange={handleInputChange}
                        className={cn(
                          "pl-10 bg-dark-300 border-dark-400 text-white",
                          "focus:border-gold-400 focus:ring-gold-400/20"
                        )}
                      />
                    </div>
                  </div>
                </div>

                {/* Max Participants */}
                <div className="space-y-2">
                  <Label className={cn("flex items-center", isDarkMode ? "text-white" : "text-dark-800")}>
  Maximum Participants

                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-dark-800" />
                    <Input
                      type="number"
                      name="maxParticipants"
                      value={formData.maxParticipants || ''}
                      onChange={handleInputChange}
                      onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.currentTarget.select()}
                      min={2}
                      max={50}
                      className={cn(
                        "pl-10 bg-dark-300 border-dark-400 text-white",
                        "focus:border-gold-400 focus:ring-gold-400/20"
                      )}
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label className={cn("flex items-center", isDarkMode ? "text-white" : "text-dark-800")}>
  Description

                    <Info className="h-4 w-4 ml-1 text-dark-800 cursor-help" />
                  </Label>
                  <Textarea
                    name="description"
                    value={formData.description || ''}
                    onChange={handleInputChange}
                    placeholder="Add details about your group activity..."
                    className={cn(
                      "bg-dark-300 border-dark-400 text-white min-h-[100px]",
                      "focus:border-gold-400 focus:ring-gold-400/20"
                    )}
                  />
                </div>

                <Button
                  onClick={handleNextStep}
                  disabled={!validateDetailsForm()}
                  className="w-full bg-gold-400 hover:bg-gold-500 text-dark-50"
                >
                  Next: Location Details
                </Button>
              </div>
            )}

            {step === 'venue' && (
              <div className="space-y-4">
                {/* City */}
                <div className="space-y-2">
                 <Label className={cn("flex items-center", isDarkMode ? "text-white" : "text-dark-800")}>
  City

                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <CitySearchCombobox
                    value={venueData.venueCity}
                    onChange={handleCityChange}
                    placeholder="Enter city..."
                    className="[&_input]:bg-dark-300 [&_input]:border-dark-400 [&_input]:text-white"
                  />
                </div>

                  {/* Club or Address picker */}
    <div className="space-y-2">
      <Label className={cn("flex items-center", isDarkMode ? "text-white" : "text-dark-800")}>
        Venue<span className="text-red-500 ml-1">*</span>
      </Label>
      <UnifiedVenueSelector
        city={venueData.venueCity}
        value={venueData.venueName}
        placeholder="Search for a club or address…"
        className="[&_input]:bg-dark-300 [&_input]:border-dark-400 [&_input]:text-white"
        onChange={v =>
          setVenueData({
            venueName: v.name,
            venueAddress: v.address,
            venueCity: v.city
          })
        }
      />
    </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button
                    onClick={handlePrevStep}
                    variant="outline"
                    className="border-dark-400 text-white hover:bg-dark-400"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={!validateVenueForm() || isSubmitting}
                    className="bg-gold-400 hover:bg-gold-500 text-dark-50 flex-1"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Group Activity'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
export default NewGroupActivityModal;